import { Play, X } from "lucide-react";
import { useEffect, useState } from "react";
import { getTrackDetail } from "../services/soundcloud/api";
import MusicCard from "../components/cards/MusicCard";
import "../styles/views/discovery-view.css";

function DiscoveryView({
  genres = [],
  chartSelections = [],
  selectedGenreId = "",
  activeReleases = [],
  onSelectGenre,
  onOpenPlaylist,
  selectedPlaylist,
  playlistTracks = [],
  isLoadingPlaylist = false,
  playlistError = "",
  newReleases = [],
  recommendedCards = [],
  isLoading = false,
  error = "",
  onRetry,
  onPlayTrack,
}) {
  const [isPlaylistModalOpen, setIsPlaylistModalOpen] = useState(false);
  const [trackDetail, setTrackDetail] = useState({
    isOpen: false,
    status: "idle",
    data: null,
    error: "",
  });
  const releaseSource = activeReleases.length ? activeReleases : newReleases;
  const totalReleaseTracks = releaseSource.reduce(
    (sum, item) => sum + (Number(item?.trackCount) || 0),
    0
  );

  useEffect(() => {
    if (selectedPlaylist && !playlistError) {
      setIsPlaylistModalOpen(true);
    }
  }, [playlistError, selectedPlaylist]);

  function formatDuration(value) {
    if (!value) return "--:--";
    if (typeof value === "string" && value.includes(":")) return value;
    const ms = Number(value);
    if (!Number.isFinite(ms) || ms <= 0) return "--:--";
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  function formatCount(value) {
    const count = Number(value);
    if (!Number.isFinite(count) || count < 0) return "N/A";
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return String(count);
  }

  function valueOrNA(value) {
    if (value === undefined || value === null || value === "") return "N/A";
    return String(value);
  }

  function isTrackUnavailable(track) {
    const transcodings = Array.isArray(track?.media?.transcodings) ? track.media.transcodings : [];
    const hasTranscodings = transcodings.length > 0;
    const allSnipped = hasTranscodings && transcodings.every((item) => Boolean(item?.snipped));
    const policy = String(track?.policy || "").toLowerCase();
    const policyRestricted = policy.includes("snip") || policy.includes("blocked");
    return allSnipped || (policyRestricted && hasTranscodings);
  }

  async function openTrackDetails(trackLike) {
    const id = Number(trackLike?.id);
    if (!Number.isInteger(id) || id <= 0) return;

    setTrackDetail({
      isOpen: true,
      status: "loading",
      data: null,
      error: "",
    });

    try {
      const detail = await getTrackDetail(id);
      setTrackDetail({
        isOpen: true,
        status: "ready",
        data: detail,
        error: "",
      });
    } catch (detailError) {
      setTrackDetail({
        isOpen: true,
        status: "error",
        data: null,
        error: detailError?.message || "Unable to load track details.",
      });
    }
  }

  function closeTrackDetails() {
    setTrackDetail({
      isOpen: false,
      status: "idle",
      data: null,
      error: "",
    });
  }

  if (error) {
    return (
      <section className="glass-panel panel-block home-state">
        <h3 className="section-title">Unable to load Discovery</h3>
        <p>{error}</p>
        <button type="button" className="hero-play-btn" onClick={onRetry}>
          Retry
        </button>
      </section>
    );
  }

  return (
    <>
      <section className="glass-panel panel-block">
        <h3 className="section-title">Trending Genres</h3>
        <div className="chip-grid">
          {(isLoading ? new Array(8).fill(null) : genres).map((genre, index) => (
            genre ? (
            <button
              type="button"
              className={`genre-chip${selectedGenreId && chartSelections[index]?.id === selectedGenreId ? " is-active" : ""}`}
              key={genre.id || genre.name}
              onClick={() => onSelectGenre?.(chartSelections[index]?.id)}
            >
              <div className="genre-chip-media">
                <img src={genre.image} alt={genre.name} loading="lazy" />
                <div className="genre-chip-overlay">
                  <p>{genre.name}</p>
                  <div className="genre-chip-meta">
                    <span>{genre.source}</span>
                    <small>{genre.tracks}</small>
                  </div>
                </div>
              </div>
            </button>
            ) : (
              <article key={`genre-skeleton-${index}`} className="genre-chip genre-chip-skeleton card-skeleton" />
            )
          ))}
        </div>
      </section>

      <section className="glass-panel panel-block">
        <h3 className="section-title">New Releases</h3>
        <div className="release-list">
          {(isLoading ? new Array(8).fill(null) : releaseSource).map((release, index) => (
            release ? (
            <button
              type="button"
              className={`release-row${selectedPlaylist?.id === release.id ? " is-active" : ""}`}
              key={release.title}
              onClick={() => {
                setIsPlaylistModalOpen(true);
                onOpenPlaylist?.(release.id);
              }}
            >
              <div className="release-row-left">
                <div className="release-row-media">
                  <img src={release.image} alt={release.title} loading="lazy" />
                </div>
                <div className="release-row-info">
                  <p>{release.title}</p>
                  <span>{release.creator}</span>
                </div>
              </div>
              <span>
                {release.year ? `${release.year} · ` : ""}
                {release.trackCount ? `${release.trackCount} tracks` : "Playlist"}
              </span>
            </button>
            ) : (
              <article key={`release-skeleton-${index}`} className="release-row release-row-skeleton card-skeleton" />
            )
          ))}
        </div>
      </section>

      <section className="glass-panel panel-block discovery-highlights">
        <h3 className="section-title">Discovery Highlights</h3>
        {isLoading ? (
          <div className="discovery-highlight-grid">
            {new Array(3).fill(null).map((_, index) => (
              <article key={`discover-highlight-skeleton-${index}`} className="discovery-highlight-card card-skeleton" />
            ))}
          </div>
        ) : (
          <div className="discovery-highlight-grid">
            <article className="discovery-highlight-card">
              <p>Selected Genre</p>
              <strong>
                {genres.find((_, index) => chartSelections[index]?.id === selectedGenreId)?.name || "Top charts"}
              </strong>
            </article>
            <article className="discovery-highlight-card">
              <p>Playlists Ready</p>
              <strong>{releaseSource.length}</strong>
            </article>
            <article className="discovery-highlight-card">
              <p>Total Tracks</p>
              <strong>{totalReleaseTracks}</strong>
            </article>
          </div>
        )}
      </section>

      {isLoading || recommendedCards.length ? (
        <section>
          <h3 className="section-title">Explore More</h3>
          <div className="card-grid three-col">
            {(isLoading ? new Array(9).fill(null) : recommendedCards).map((card, index) => (
              card ? (
              <MusicCard
                key={card.id}
                {...card}
                onPlay={() => onPlayTrack?.(card, recommendedCards, index)}
              />
              ) : (
                <article key={`discover-card-skeleton-${index}`} className="music-card glass-panel card-skeleton" />
              )
            ))}
          </div>
        </section>
      ) : null}

      {isPlaylistModalOpen ? (
        <div className="discovery-modal-overlay" onClick={() => setIsPlaylistModalOpen(false)}>
          <article
            className="discovery-modal glass-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Discovery playlist tracks"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="discovery-modal-head">
              <h3>{selectedPlaylist?.title || "Playlist tracks"}</h3>
              <button
                type="button"
                className="discovery-modal-close"
                aria-label="Close playlist tracks"
                onClick={() => setIsPlaylistModalOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            <p className="discovery-modal-subtitle">
              {isLoadingPlaylist
                ? "Loading tracks..."
                : selectedPlaylist
                  ? `${selectedPlaylist.creator} · ${selectedPlaylist.trackCount} tracks`
                  : "Select a playlist to view tracks"}
            </p>

            {playlistError ? <p className="home-state-inline">{playlistError}</p> : null}

            <div className="discovery-track-list">
              {isLoadingPlaylist ? (
                <div className="detail-loading discovery-playlist-loading">
                  <div className="detail-loading-head">
                    <div className="detail-loading-avatar shimmer-block" />
                    <div className="detail-loading-head-text">
                      <span className="shimmer-block detail-loading-line lg" />
                      <span className="shimmer-block detail-loading-line md" />
                    </div>
                  </div>

                  <div className="detail-loading-grid">
                    {new Array(4).fill(null).map((_, index) => (
                      <span key={`discover-grid-${index}`} className="shimmer-block detail-loading-pill" />
                    ))}
                  </div>

                  <div className="detail-loading-list">
                    {new Array(8).fill(null).map((_, index) => (
                      <span key={`discover-row-${index}`} className="shimmer-block detail-loading-row" />
                    ))}
                  </div>

                  <p className="detail-loading-text">Loading playlist details...</p>
                </div>
              ) : (
                (playlistTracks || []).map((track, index) => (
                  <article
                    key={`${track.id}-${index}`}
                    className={`discovery-track-row${track.isUnavailable ? " is-unavailable" : ""}`}
                  >
                    <button
                      type="button"
                      className="discovery-track-main discovery-track-detail-btn"
                      onClick={() => openTrackDetails(track)}
                    >
                      <p>{track.title}</p>
                      <span className={track.isUnavailable ? "is-warning" : ""}>
                        {track.isUnavailable
                          ? track.unavailableText || "Not available in your country"
                          : track.artist}
                      </span>
                    </button>
                    <div className="discovery-track-actions">
                      <span>{track.isUnavailable ? "Blocked" : formatDuration(track.duration)}</span>
                      <button
                        type="button"
                        className="queue-icon-btn"
                        disabled={track.isUnavailable}
                        onClick={() => onPlayTrack?.(track, playlistTracks, index)}
                        aria-label={track.isUnavailable ? `${track.title} unavailable` : `Play ${track.title}`}
                      >
                        <Play size={13} />
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </article>
        </div>
      ) : null}

      {trackDetail.isOpen ? (
        <div className="discovery-modal-overlay" onClick={closeTrackDetails}>
          <article
            className="discovery-modal glass-panel discovery-track-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Track details"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="discovery-modal-head">
              <h3>Track details</h3>
              <button
                type="button"
                className="discovery-modal-close"
                aria-label="Close track details"
                onClick={closeTrackDetails}
              >
                <X size={16} />
              </button>
            </div>

            {trackDetail.status === "loading" ? (
              <p className="discovery-modal-subtitle">Loading details...</p>
            ) : null}

            {trackDetail.status === "error" ? (
              <p className="home-state-inline">{trackDetail.error || "Unable to load track details."}</p>
            ) : null}

            {trackDetail.status === "ready" && trackDetail.data ? (
              <div className="discovery-track-detail-wrap">
                <h4>{valueOrNA(trackDetail.data.title)}</h4>
                <div className="discovery-track-detail-actions">
                  <button
                    type="button"
                    className="hero-play-btn"
                    disabled={isTrackUnavailable(trackDetail.data)}
                    onClick={() => onPlayTrack?.(trackDetail.data, [trackDetail.data], 0)}
                  >
                    <Play size={15} />
                    {isTrackUnavailable(trackDetail.data)
                      ? "Not available in your country"
                      : "Play track"}
                  </button>
                </div>

                <div className="discovery-track-detail-grid">
                  <p><strong>Artist:</strong> {valueOrNA(trackDetail.data?.user?.username)}</p>
                  <p><strong>Duration:</strong> {formatDuration(trackDetail.data?.duration)}</p>
                  <p><strong>Genre:</strong> {valueOrNA(trackDetail.data?.genre)}</p>
                  <p><strong>Plays:</strong> {formatCount(trackDetail.data?.playback_count)}</p>
                  <p><strong>Likes:</strong> {formatCount(trackDetail.data?.likes_count)}</p>
                  <p><strong>Reposts:</strong> {formatCount(trackDetail.data?.reposts_count)}</p>
                  <p><strong>Comments:</strong> {formatCount(trackDetail.data?.comment_count)}</p>
                  <p><strong>Policy:</strong> {valueOrNA(trackDetail.data?.policy)}</p>
                </div>

                <div className="discovery-track-detail-bio">
                  <p className="user-detail-label">Description</p>
                  <p>{valueOrNA(trackDetail.data?.description)}</p>
                </div>
              </div>
            ) : null}
          </article>
        </div>
      ) : null}
    </>
  );
}

export default DiscoveryView;
